import {
  createContext,
  useContext,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction,
} from "react";

import { InspectionPlan, InspectionUnit, InspectionCriteria } from "./types";

interface InspectionContextType {
  currentPlan: InspectionPlan | null;
  setCurrentPlan: Dispatch<SetStateAction<InspectionPlan | null>>;
  inspectionUnits: InspectionUnit[];
  setInspectionUnits: Dispatch<SetStateAction<InspectionUnit[]>>;
  inspectionCriteria: InspectionCriteria[];
  setInspectionCriteria: Dispatch<SetStateAction<InspectionCriteria[]>>;
  currentUnitIndex: number;
  setCurrentUnitIndex: Dispatch<SetStateAction<number>>;
}

const InspectionContext = createContext<InspectionContextType | undefined>(
  undefined,
);

export function InspectionProvider({ children }: { children: ReactNode }) {
  const [currentPlan, setCurrentPlan] = useState<InspectionPlan | null>({
    id: "INSP-2026-00031",
    scope: "IN",
    documentNo: "IN-2026-00031",
    item: "AL-Plate",
    quantity: 30,
    lot: "L-2401",
    inspectionType: "입고A",
    version: "v3",
    samplingMethod: "SAMPLE",
    sampleSize: 5,
    sampleList: ["LBL-0007", "LBL-0012", "LBL-0018", "LBL-0021", "LBL-0029"],
    status: "IN_PROGRESS",
  });

  const [inspectionCriteria, setInspectionCriteria] = useState<
    InspectionCriteria[]
  >([
    {
      id: "c1",
      name: "외관 검사",
      type: "boolean",
      required: true,
      spec: "흠집, 변색 없음",
    },
    {
      id: "c2",
      name: "치수 측정(mm)",
      type: "number",
      required: true,
      spec: "±0.5mm",
      min: 99.5,
      max: 100.5,
    },
    {
      id: "c3",
      name: "평탄도",
      type: "select",
      required: true,
      options: ["양호", "보통", "불량"],
    },
    {
      id: "c4",
      name: "표면 처리",
      type: "select",
      required: true,
      options: ["양호", "불량"],
    },
    {
      id: "c5",
      name: "포장 상태",
      type: "boolean",
      required: false,
      spec: "파손 없음",
    },
    {
      id: "c6",
      name: "라벨 확인",
      type: "boolean",
      required: true,
      spec: "QR코드 스캔 가능",
    },
    {
      id: "c7",
      name: "사진 첨부",
      type: "photo",
      required: true,
      photoRequired: true,
    },
    { id: "c8", name: "코멘트", type: "description", required: false },
  ]);

  const [inspectionUnits, setInspectionUnits] = useState<InspectionUnit[]>([
    { id: "u1", label: "LBL-0007", status: "DONE", data: {}, hasDefect: false },
    { id: "u2", label: "LBL-0012", status: "DONE", data: {}, hasDefect: false },
    {
      id: "u3",
      label: "LBL-0018",
      status: "IN_PROGRESS",
      data: {},
      hasDefect: false,
    },
    {
      id: "u4",
      label: "LBL-0021",
      status: "NOT_STARTED",
      data: {},
      hasDefect: false,
    },
    {
      id: "u5",
      label: "LBL-0029",
      status: "DEFECT",
      data: {},
      hasDefect: true,
      defectInfo: {
        type: "표면 불량",
        severity: "MAJOR",
        isolated: true,
        photos: ["defect-1.jpg"],
        memo: "표면에 깊은 스크래치 발견",
      },
    },
  ]);

  const [currentUnitIndex, setCurrentUnitIndex] = useState(2);

  return (
    <InspectionContext.Provider
      value={{
        currentPlan,
        setCurrentPlan,
        inspectionUnits,
        setInspectionUnits,
        inspectionCriteria,
        setInspectionCriteria,
        currentUnitIndex,
        setCurrentUnitIndex,
      }}
    >
      {children}
    </InspectionContext.Provider>
  );
}

export function useInspection() {
  const context = useContext(InspectionContext);
  if (context === undefined) {
    throw new Error("useInspection must be used within an InspectionProvider");
  }
  return context;
}
